import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { uploadRecording, fetchRecordingAudioUrl } from "../api/recordings";
import { swapCampaignRecording, type RecordingSlot } from "../api/campaigns";

/**
 * Replace the audio a campaign plays, without stopping it.
 *
 * Upload converts and publishes the file, then the campaign is repointed at it —
 * no file is overwritten, so a call playing the old audio right now is unaffected
 * and recordings shared with other campaigns stay as they are.
 *
 * The new audio is staged and played back before anything changes: a wrong take
 * would otherwise reach live callers seconds later, and the only way back is
 * another upload.
 */

interface Props {
  campaignId: number;
  slot: RecordingSlot;
  /** Shown so the operator knows what they're replacing. */
  label: string;
  /** Live campaigns pick up the change on the runner's next config refresh. */
  isRunning: boolean;
  onSwapped?: (ref: string) => void;
}

type Staged = { ref: string; filename: string; durationSec: number | null };

function fmtDuration(sec: number | null): string {
  if (sec == null) return "";
  return `${sec.toFixed(1)}s`;
}

export function ReplaceCampaignAudio({ campaignId, slot, label, isRunning, onSwapped }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  function readError(e: unknown): string {
    const ax = e as { response?: { data?: { error?: string; detail?: string } }; message?: string };
    return ax.response?.data?.error || ax.response?.data?.detail || ax.message || "Something went wrong";
  }

  // Step 1 — convert and publish, but don't touch the campaign yet.
  const upload = useMutation({
    mutationFn: (file: File) => uploadRecording(file),
    onSuccess: (r) => {
      setError(null);
      setStaged({ ref: r.ref, filename: r.filename, durationSec: r.duration_sec });
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
    onError: (e) => setError(readError(e)),
  });

  // Step 2 — only now does anything change for callers.
  const swap = useMutation({
    mutationFn: (ref: string) => swapCampaignRecording(campaignId, slot, ref),
    onSuccess: (r) => {
      setError(null);
      setStaged(null);
      setDone(
        r.live_within_seconds > 0
          ? `Live on new calls within ${r.live_within_seconds} seconds.`
          : "Saved. It will play when the campaign runs.",
      );
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onSwapped?.(r.ref);
    },
    onError: (e) => setError(readError(e)),
  });

  async function preview() {
    if (!staged || !audioRef.current) return;
    try {
      audioRef.current.src = await fetchRecordingAudioUrl(staged.filename);
      await audioRef.current.play();
    } catch {
      setError("Couldn't play that back here, but the file converted fine.");
    }
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setDone(null);
    upload.mutate(file);
    e.target.value = "";
  }

  const busy = upload.isPending || swap.isPending;

  return (
    <div className="mt-2">
      <input
        ref={fileRef}
        type="file"
        accept="audio/*,video/*,.wav,.mp3,.gsm,.ulaw,.alaw,.m4a,.aac,.ogg,.opus,.flac,.wma,.amr,.aiff"
        className="hidden"
        onChange={onPick}
      />
      <audio ref={audioRef} className="hidden" />

      {!staged && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
        >
          {upload.isPending ? "Converting…" : "Replace audio"}
        </button>
      )}

      {staged && (
        <div className="border border-amber-300 bg-amber-50 rounded p-2 text-xs">
          <p className="font-medium text-gray-800">
            Ready: {staged.filename} {staged.durationSec != null && `· ${fmtDuration(staged.durationSec)}`}
          </p>
          <p className="text-gray-600 mt-0.5">
            {isRunning
              ? `Listen first — this campaign is live, and callers will hear it within 30 seconds of confirming.`
              : `Listen first, then confirm to set it as the ${label.toLowerCase()}.`}
          </p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={preview}
              className="border border-gray-300 bg-white rounded px-2 py-1 hover:bg-gray-50"
            >
              ▶ Play
            </button>
            <button
              type="button"
              onClick={() => swap.mutate(staged.ref)}
              disabled={busy}
              className="bg-brand hover:bg-brand-dark text-white rounded px-2 py-1 disabled:opacity-50"
            >
              {swap.isPending ? "Applying…" : isRunning ? "Use this on live calls" : "Use this"}
            </button>
            <button
              type="button"
              onClick={() => { setStaged(null); setError(null); }}
              disabled={busy}
              className="text-gray-600 px-2 py-1 hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {done && <p className="text-xs text-green-700 mt-1">{done}</p>}
    </div>
  );
}
